import React from 'react';
import { Music, ShieldCheck, Sparkle, Star } from 'lucide-react';
import { hasBardFeature, getBardLevel, heldBardicDie } from '../../../shared/rules/bard.mjs';
import { jackOfAllTradesBonus } from '../../../shared/rules/classExpertise';
import { isIncapacitated } from '../../../shared/rules/barbarianRage.mjs';
import { notify } from '../../../utils/notification';

/**
 * Reminder tiles for the Bard features that work on their own (2024), like the Cleric's passive tiles, and the die a
 * creature of any class holds. Nothing is spent here: the die is spent from the roll prompts and the roll log.
 */
export const bardPassiveTiles = (form, characterId, combatState, proficiencyBonus) => {
  const incapacitated = isIncapacitated({ ...(form || {}), combatantId: String(characterId) }, combatState || {});
  const held = heldBardicDie(form);
  const jack = jackOfAllTradesBonus(form, proficiencyBonus, false);
  return [
    held && { id: 'held-die', name: `Inspired by ${held.fromName}`, Icon: Music, accent: '#d7b46a', lit: true, value: `d${held.sides}`,
      about: `When you fail a D20 Test in the next hour, you can roll this d${held.sides} and add it to the d20. Any rest ends it, as does the DM passing an hour.` },
    getBardLevel(form) >= 2 && jack > 0 && { id: 'jack-of-all-trades', name: 'Jack of All Trades', Icon: Sparkle, accent: '#9ad1a0', lit: !incapacitated, value: `+${jack}`,
      about: `Your skill checks without proficiency add +${jack}, half your Proficiency Bonus. It's already in your skill numbers. Tools, Initiative and plain ability checks don't get it.` },
    hasBardFeature(form, 'countercharm') && { id: 'countercharm', name: 'Countercharm', Icon: ShieldCheck, accent: '#9fd3ff', lit: !incapacitated, value: '30 ft',
      about: 'When you or a creature within 30 feet fails a save against an effect that applies Charmed or Frightened, you are asked whether to take a Reaction so it can reroll with Advantage.' },
    hasBardFeature(form, 'superiorInspiration') && { id: 'superior-inspiration', name: 'Superior Inspiration', Icon: Star, accent: '#c9b6ff', lit: !incapacitated, value: 'Up to 2',
      about: 'When you roll Initiative, you regain Bardic Inspiration uses until you have two, if you have fewer.' },
  ].filter(Boolean);
};

export default function BardPassiveTiles({ form, characterId, combatState, proficiencyBonus }) {
  const tiles = bardPassiveTiles(form, characterId, combatState, proficiencyBonus);
  if (!tiles.length) return null;
  return <>{tiles.map(({ id, name, Icon, accent, lit, value, about }) => (
    <button key={id} type="button" data-action-tile={`bard:${id}`} aria-label={`${name}: ${lit ? 'active' : 'inactive'}`} title={about}
      className={`combat-hud-resource-tile combat-hud-feature-action-tile combat-hud-resource-tile--passive${lit ? '' : ' combat-hud-resource-tile--dormant'}`}
      style={{ '--hud-resource-accent': accent }} onClick={() => notify(`${name}: ${about}`, 'info')}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Icon size={20}/></span>
      <span className="combat-hud-resource-tile__name">{name}</span>
      <span className="combat-hud-resource-tile__value">{value}</span>
    </button>
  ))}</>;
}
